import './SupportChat.scss';
import {
    CSSProperties,
    Fragment,
    KeyboardEvent,
    memo,
    useEffect,
    useId,
    useLayoutEffect,
    useRef,
    useState,
    type FormEvent,
} from 'react';
import clsx from 'clsx';
import { CloseButton, VisuallyHidden } from '@mantine/core';
import { MdSend } from 'react-icons/md';
import { participantColor } from '@srm/screen-sharing/light';
import { getInitials } from '@srm/ui/avatar';
import { sameChatGroup, typingText } from '@srm/ui/lib/chatText';
import type { ISupportBarPerson } from '@molecules/SupportBar';
import { withPersonCard } from '@molecules/SupportBar/withPersonCard';
import UserHoverCard from '@molecules/UserHoverCard/UserHoverCard';
import {
    SUPPORT_CHAT_LIMIT,
    SUPPORT_CHAT_NOT_READY_TEXT,
    type ISupportChatMessage,
} from '@providers/ScreenShareProvider/supportChat';

/** Счётчик символов появляется, когда до предела осталось столько. */
const COUNTER_FROM = SUPPORT_CHAT_LIMIT - 200;
/** Ближе к низу ленты, чем столько, — новые сообщения прокручивают её вниз. */
const STICK_DISTANCE = 48;

export interface ISupportChatProps {
    messages: readonly ISupportChatMessage[];
    /** Кто из специалистов печатает */
    typing?: readonly string[];
    /** Своё сообщение с отметкой «прочитано» (последнее прочитанное специалистами) */
    readKey?: string | null;
    /** Специалисты показа — кружки в шапке */
    people?: readonly ISupportBarPerson[];
    /** Специалист ещё не подключился: писать можно, сообщение он увидит */
    waiting?: boolean;
    /** `false` — не принято (показ ещё не начался на сервере): текст остаётся в поле, под ним — почему */
    onSend: (text: string) => boolean | void;
    /** Набранный текст снаружи — переживает сворачивание панели; без него поле хранит его само */
    draft?: string;
    onDraftChange?: (draft: string) => void;
    onRetry: (key: string) => void;
    /** Пользователь набирает текст (сторона сама прореживает `ChatTyping`) */
    onTyping?: () => void;
    /** «Свернуть» и Escape */
    onClose: () => void;
    /** Фокус в поле ввода при появлении */
    autoFocus?: boolean;
    className?: string;
}

const timeOf = (at: string) => {
    const date = new Date(at);
    return Number.isNaN(date.getTime()) ? '' : date.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' });
};

const colorOf = (message: ISupportChatMessage) =>
    message.color === undefined ? 'var(--neutral-50)' : participantColor(message.color);

/**
 * Подряд идущие сообщения одного автора (в пределах дня и 5 мин, как в чате специалиста — `sameChatGroup`): имя —
 * один раз над группой, время — у последнего.
 */
interface IMessageGroup {
    key: string;
    mine: boolean;
    messages: ISupportChatMessage[];
}

function groupMessages(messages: readonly ISupportChatMessage[]): IMessageGroup[] {
    const groups: IMessageGroup[] = [];
    for (const message of messages) {
        const group = groups.at(-1);
        if (group && sameChatGroup(group.messages.at(-1)!, message)) group.messages.push(message);
        else groups.push({ key: message.key, mine: message.mine, messages: [message] });
    }
    return groups;
}

/**
 * Чат показа экрана у пользователя (IMPLEMENTATION.md §30, prototype-stage5.html «Пользователь: чат и микрофон»):
 * подряд идущие сообщения автора — группой (имя и цвет специалиста над ней, время у последнего), «печатает…»,
 * «прочитано» у последнего своего прочитанного, «отправляется…» и «не отправлено — повторить».
 * Enter отправляет, Shift+Enter — новая строка. Помечен `data-remote-control-exclude`: удалённое управление в чат не
 * пишет, а пользователь пишет в него и во время управления.
 */
const SupportChat = ({
    messages,
    typing = [],
    readKey,
    people = [],
    waiting = false,
    onSend,
    onRetry,
    onTyping,
    onClose,
    autoFocus = false,
    draft: outerDraft,
    onDraftChange,
    className,
}: ISupportChatProps) => {
    const titleId = useId();
    const hintId = useId();
    const [ownDraft, setOwnDraft] = useState('');
    const draft = outerDraft ?? ownDraft;
    const setDraft = (value: string) => {
        setOwnDraft(value);
        onDraftChange?.(value);
    };
    const [notReady, setNotReady] = useState(false);
    const list = useRef<HTMLDivElement>(null);
    const input = useRef<HTMLTextAreaElement>(null);
    const root = useRef<HTMLElement>(null);
    const stick = useRef(true);
    const sentByMe = useRef(false);
    const close = useRef(onClose);
    close.current = onClose;

    // Escape в панели сворачивает её (во время управления Escape забирает управление раньше — в фазе перехвата).
    useEffect(() => {
        const panel = root.current;
        if (!panel) return undefined;
        const onKey = (event: globalThis.KeyboardEvent) => {
            if (event.key !== 'Escape' || event.defaultPrevented) return;
            event.preventDefault();
            close.current();
        };
        panel.addEventListener('keydown', onKey);
        return () => panel.removeEventListener('keydown', onKey);
    }, []);

    useEffect(() => {
        if (autoFocus) input.current?.focus();
    }, [autoFocus]);

    // Новое сообщение прокручивает ленту вниз, если пользователь и так внизу или отправил его сам.
    const last = messages.at(-1)?.key;
    useLayoutEffect(() => {
        const box = list.current;
        if (!box || !(stick.current || sentByMe.current)) return;
        sentByMe.current = false;
        box.scrollTop = box.scrollHeight;
    }, [last, messages.length, typing.length]);

    // Поле растёт по тексту до предела высоты из стилей.
    useLayoutEffect(() => {
        const field = input.current;
        if (!field) return;
        field.style.height = 'auto';
        // `border-box`: к высоте содержимого — рамки, иначе у одной строки появляется прокрутка.
        field.style.height = `${field.scrollHeight + field.offsetHeight - field.clientHeight}px`;
    }, [draft]);

    const text = draft.trim();
    const submit = (event?: FormEvent) => {
        event?.preventDefault();
        if (!text) return;
        if (onSend(draft) === false) {
            setNotReady(true);
            return;
        }
        setNotReady(false);
        sentByMe.current = true;
        setDraft('');
    };
    const onKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
        if (event.key !== 'Enter' || event.shiftKey || event.nativeEvent.isComposing) return;
        event.preventDefault();
        submit();
    };

    const typingLine = typingText(typing);
    return (
        <section
            className={clsx('support-chat', className)}
            aria-labelledby={titleId}
            ref={root}
            data-remote-control-exclude
            data-testid="support-chat"
        >
            <header className="support-chat__head">
                {people.length > 0 && (
                    <span className="support-chat__people" aria-hidden>
                        {people.map((person, index) => (
                            <Fragment key={`${person.name}-${index}`}>
                                {withPersonCard(
                                    person.userKey,
                                    person.name,
                                    <span
                                        className="support-chat__person"
                                        title={person.userKey ? undefined : person.name}
                                        style={{ '--support-chat-person': person.color } as CSSProperties}
                                    >
                                        {getInitials(person.name)}
                                    </span>,
                                )}
                            </Fragment>
                        ))}
                    </span>
                )}
                <h2 className="support-chat__title" id={titleId}>
                    Чат с поддержкой
                </h2>
                <CloseButton size="md" aria-label="Свернуть чат" title="Свернуть" onClick={onClose} />
            </header>
            <div
                ref={list}
                className="support-chat__messages"
                role="log"
                aria-live="polite"
                aria-relevant="additions"
                aria-label="Сообщения"
                onScroll={(event) => {
                    const box = event.currentTarget;
                    stick.current = box.scrollHeight - box.scrollTop - box.clientHeight < STICK_DISTANCE;
                }}
            >
                <p className="support-chat__system">
                    {waiting
                        ? 'Сотрудник поддержки ещё не подключился. Напишите, в чём вопрос, — он увидит сообщение.'
                        : 'Переписка с поддержкой, пока идёт показ экрана. Она сохранится в истории сеанса.'}
                </p>
                {groupMessages(messages).map((group) => {
                    const head = group.messages[0];
                    return (
                        <div
                            key={group.key}
                            className={clsx('support-chat__group', group.mine && 'support-chat__group--mine')}
                            data-testid="support-chat-group"
                            style={{ '--support-chat-author': colorOf(head) } as CSSProperties}
                        >
                            {!group.mine && (
                                <span className="support-chat__author">
                                    <i aria-hidden />
                                    {head.role === 'support' && head.authorId ? (
                                        <UserHoverCard userKey={head.authorId} fallbackName={head.authorName} inline>
                                            {head.authorName}
                                        </UserHoverCard>
                                    ) : (
                                        head.authorName
                                    )}
                                </span>
                            )}
                            {group.messages.map((message, index) => (
                                <div
                                    key={message.key}
                                    className="support-chat__message"
                                    data-status={message.status}
                                    data-testid="support-chat-message"
                                >
                                    <span className="support-chat__bubble">
                                        {!message.mine && index > 0 && (
                                            <VisuallyHidden component="span">{`${message.authorName}: `}</VisuallyHidden>
                                        )}
                                        {message.text}
                                    </span>
                                    <MessageMeta
                                        message={message}
                                        last={index === group.messages.length - 1}
                                        read={message.key === readKey}
                                        onRetry={() => onRetry(message.key)}
                                    />
                                </div>
                            ))}
                        </div>
                    );
                })}
            </div>
            <div className="support-chat__typing" aria-hidden data-testid="support-chat-typing">
                {typingLine && (
                    <>
                        <span className="support-chat__dots">
                            <i />
                            <i />
                            <i />
                        </span>
                        {typingLine}
                    </>
                )}
            </div>
            <form className="support-chat__composer" onSubmit={submit}>
                <div className="support-chat__row">
                    <textarea
                        ref={input}
                        className="support-chat__input"
                        rows={1}
                        value={draft}
                        maxLength={SUPPORT_CHAT_LIMIT}
                        placeholder="Сообщение поддержке…"
                        aria-label="Сообщение поддержке"
                        aria-describedby={hintId}
                        onChange={(event) => {
                            setDraft(event.target.value);
                            setNotReady(false);
                            if (event.target.value.trim()) onTyping?.();
                        }}
                        onKeyDown={onKeyDown}
                    />
                    <button
                        type="submit"
                        className="support-chat__send"
                        aria-label="Отправить"
                        title="Отправить (Enter)"
                        disabled={!text}
                    >
                        <MdSend size={18} aria-hidden />
                    </button>
                </div>
                {notReady && (
                    <span className="support-chat__notice" role="status" data-testid="support-chat-not-ready">
                        {SUPPORT_CHAT_NOT_READY_TEXT}
                    </span>
                )}
                <span className="support-chat__hint" id={hintId}>
                    Enter — отправить, Shift+Enter — новая строка
                    {draft.length > COUNTER_FROM && (
                        <span className="support-chat__counter" data-testid="support-chat-counter">
                            {draft.length} / {SUPPORT_CHAT_LIMIT}
                        </span>
                    )}
                </span>
            </form>
        </section>
    );
};

const MessageMeta = ({
    message,
    last,
    read,
    onRetry,
}: {
    message: ISupportChatMessage;
    /** Последнее в группе — с временем */
    last: boolean;
    read: boolean;
    onRetry: () => void;
}) => {
    if (message.status === 'sending') return <span className="support-chat__meta">отправляется…</span>;
    if (message.status === 'failed')
        return (
            <span className="support-chat__meta">
                <button
                    type="button"
                    className="support-chat__retry"
                    title={message.error}
                    aria-label={`Не отправлено${message.error ? `: ${message.error}` : ''}. Повторить`}
                    onClick={onRetry}
                >
                    Не отправлено · повторить
                </button>
            </span>
        );
    const time = last ? timeOf(message.at) : '';
    if (!time && !read) return null;
    return <span className="support-chat__meta">{[time, read && 'прочитано'].filter(Boolean).join(' · ')}</span>;
};

/** Пропсы панели стабильны: перерисовка — только когда меняется переписка, а не всё состояние показа. */
export default memo(SupportChat);
